<script lang="ts">
export default {
  name: 'Step6ProgramGrouping',
};
</script>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, ref, watch, watchEffect } from 'vue';
import { CircleCheck, Plus } from '@lucide/vue';
import { useDataReadinessWizard } from '@/composables/useDataReadinessWizard';
import ProgramGroupingRow from '@/components/v2/data_readiness/ProgramGroupingRow.vue';
import AccountProgramForm from '@/views/account/AccountProgramForm.vue';
import AppModal from '@/components/v2/AppModal.vue';
import Toggle from '@/components/v2/Toggle.vue';
import { externalPolicyName } from '@/utilities/OrgConfig';

const wizard = useDataReadinessWizard();

onBeforeMount(() => {
  void wizard.refreshPrograms();
  void wizard.refreshPoliciesWithoutProgram();
});
watch([wizard.showCurrentOnly, wizard.showNonCAC], () => {
  void wizard.refreshPrograms();
  void wizard.refreshPoliciesWithoutProgram();
});

const accountId = wizard.accountId;

const missingRoleByProgramId = computed(() => {
  const map = new Map<number, boolean>();
  for (const v of wizard.ruleViolatingPrograms.value) map.set(v.programId, v.noServicingRolesRule);
  return map;
});

const contactByProgramId = computed(() => {
  const map = new Map<number, NonNullable<typeof wizard.programSummaries.value[number]['accountContact']>>();
  for (const s of wizard.programSummaries.value) {
    if (s.accountContact) map.set(s.programId, s.accountContact);
  }
  return map;
});

const programsMissingRoleCount = computed(
  () => wizard.ruleViolatingPrograms.value.filter((p) => p.noServicingRolesRule).length,
);
const ungroupedPolicyCount = computed(() => wizard.policiesWithoutProgram.value.length);
const totalPrograms = computed(() => wizard.programs.value.length);

const openProgramIds = ref<Set<number>>(new Set());
function toggle(programId: number) {
  const next = new Set(openProgramIds.value);
  if (next.has(programId)) next.delete(programId);
  else next.add(programId);
  openProgramIds.value = next;
}

const editingProgramId = ref<number | null>(null);
const programFormOpen = ref(false);
// Kept separate from programFormOpen so the form survives the close animation. The form is
// gated at all because AppModal always renders its content slot, and mounting it eagerly
// would fire the policy/coverage-type fetches for everyone who merely lands on Step 6.
const programFormMounted = ref(false);
const programFormRef = ref<InstanceType<typeof AccountProgramForm> | null>(null);
const programFormSaving = computed(() => programFormRef.value?.saving ?? false);

// DaisyUI fades .modal-box out over 300ms. Tearing the form down any sooner collapses the
// box to title-and-buttons height, and flips the title, while it's still on screen.
const MODAL_CLOSE_MS = 300;
let programFormTeardownId: ReturnType<typeof setTimeout> | undefined;

// Keys the form so every open mounts a fresh instance, including a reopen that lands inside
// the teardown delay and would otherwise inherit the last session's half-typed values.
const programFormInstance = ref(0);

function openProgramForm(programId: number | null) {
  clearTimeout(programFormTeardownId);
  editingProgramId.value = programId;
  programFormInstance.value += 1;
  programFormMounted.value = true;
  programFormOpen.value = true;
}

const openCreateProgram = () => openProgramForm(null);
const openEditProgram = (programId: number) => openProgramForm(programId);

function closeProgramForm() {
  programFormOpen.value = false;
  programFormTeardownId = setTimeout(() => {
    programFormMounted.value = false;
    editingProgramId.value = null;
  }, MODAL_CLOSE_MS);
}

async function onProgramSaved() {
  closeProgramForm();
  await Promise.all([wizard.refreshPrograms(), wizard.refreshPoliciesWithoutProgram()]);
}

watchEffect(() => {
  const missing = programsMissingRoleCount.value;
  const ungrouped = ungroupedPolicyCount.value;
  if (missing === 0 && ungrouped === 0) {
    wizard.setFooterHint({
      text: 'All programs and policies are grouped — ready to proceed',
      color: 'green',
    });
  } else {
    const missingText = `${missing} program${missing === 1 ? '' : 's'} missing a servicing role`;
    const ungroupedText = `${ungrouped} polic${ungrouped === 1 ? 'y' : 'ies'} not yet grouped`;
    wizard.setFooterHint({ text: `${missingText}, ${ungroupedText}`, color: 'amber' });
  }
});

onBeforeUnmount(() => {
  clearTimeout(programFormTeardownId);
  wizard.setFooterHint(null);
});
</script>

<template>
  <div class="p-6">
    <h2 class="mb-1 text-base font-semibold">Program grouping</h2>
    <p class="mb-4 text-sm text-base-content/60">
      Review the account's programs and any policies not yet grouped into one. Fixing these before go-live keeps the client portal tidy.
    </p>

    <div class="mb-4 flex gap-5">
      <Toggle
        v-model="wizard.showCurrentOnly.value"
        label="Show current only"
        size="sm" />
      <Toggle
        v-model="wizard.showNonCAC.value"
        :label="`Show ${externalPolicyName()}`"
        size="sm" />
    </div>

    <div class="mb-4 flex flex-wrap gap-2">
      <div class="flex items-center gap-1 rounded-md border border-base-300 bg-base-200 px-3 py-1.5 text-sm">
        <span class="font-semibold">{{ totalPrograms }}</span>
        <span class="text-base-content/70">total programs</span>
      </div>
      <div class="flex items-center gap-1 rounded-md border border-base-300 bg-base-200 px-3 py-1.5 text-sm">
        <span
          class="font-semibold"
          :class="programsMissingRoleCount > 0 ? 'text-error' : ''"
          >{{ programsMissingRoleCount }}</span
        >
        <span class="text-base-content/70">missing servicing role</span>
      </div>
      <div class="flex items-center gap-1 rounded-md border border-base-300 bg-base-200 px-3 py-1.5 text-sm">
        <span
          class="font-semibold"
          :class="ungroupedPolicyCount > 0 ? 'text-warning' : ''"
          >{{ ungroupedPolicyCount }}</span
        >
        <span class="text-base-content/70">ungrouped policies</span>
      </div>
    </div>

    <!-- Section 1: Programs -->
    <section class="mb-6">
      <div class="mb-2 flex items-center justify-between gap-3">
        <h3 class="text-sm font-semibold">Programs</h3>
        <button
          class="btn gap-1 btn-outline btn-xs btn-primary"
          data-testid="create-program"
          @click="openCreateProgram">
          <Plus :size="12" />
          Create program
        </button>
      </div>
      <div
        v-if="wizard.loadingPrograms.value && wizard.programs.value.length === 0"
        class="rounded-md border border-base-300 bg-base-100 px-4 py-6 text-center text-sm text-base-content/60">
        Loading programs…
      </div>
      <div
        v-else-if="wizard.programs.value.length === 0"
        class="rounded-md border border-base-300 bg-base-100 px-4 py-6 text-center text-sm">
        <div class="mb-2 text-base-content/60">No programs have been created for this client yet.</div>
        <button
          class="link link-primary text-sm font-semibold"
          data-testid="create-program-empty"
          @click="openCreateProgram">
          Create program
        </button>
      </div>
      <div
        v-else
        class="overflow-hidden rounded-md border border-base-300">
        <ProgramGroupingRow
          v-for="p in wizard.programs.value"
          :key="p.id"
          :program="p"
          :account-id="accountId"
          :servicing-role-missing="missingRoleByProgramId.get(p.id) ?? false"
          :program-contact="contactByProgramId.get(p.id) ?? null"
          :is-open="openProgramIds.has(p.id)"
          @toggle="toggle(p.id)"
          @edit="openEditProgram(p.id)" />
      </div>
    </section>

    <!-- Section 2: Policies without a program -->
    <section>
      <h3 class="mb-2 text-sm font-semibold">Policies without a program</h3>
      <div
        v-if="wizard.loadingPoliciesWithoutProgram.value && wizard.policiesWithoutProgram.value.length === 0"
        class="rounded-md border border-base-300 bg-base-100 px-4 py-6 text-center text-sm text-base-content/60">
        Loading policies…
      </div>
      <div
        v-else-if="wizard.policiesWithoutProgram.value.length === 0"
        class="flex items-center justify-center gap-2 rounded-md border border-base-300 bg-base-100 px-4 py-6 text-sm text-success">
        <CircleCheck :size="14" />
        All policies are assigned to a program
      </div>
      <div
        v-else
        class="overflow-hidden rounded-md border border-base-300">
        <table class="w-full text-sm">
          <thead class="bg-base-200 text-xs uppercase text-base-content/60">
            <tr>
              <th class="px-4 py-2 text-left">Policy number</th>
              <th class="px-4 py-2 text-left">Description</th>
              <th class="px-4 py-2 text-left">Coverage type</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="policy in wizard.policiesWithoutProgram.value"
              :key="policy.policyID"
              class="border-t border-base-300">
              <td class="px-4 py-2 font-mono text-xs">{{ policy.uniqPolicy || '—' }}</td>
              <td class="px-4 py-2">{{ policy.description || '—' }}</td>
              <td class="px-4 py-2">{{ policy.coverageType || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- max-w override: the policy-selection table needs far more room than AppModal's
         widest preset (max-w-5xl). ! wins regardless of stylesheet source order. -->
    <AppModal
      :show="programFormOpen"
      :handleCancel="closeProgramForm"
      size="xxl"
      modalClass="!max-w-[95vw]">
      <template #title>
        <h1 class="text-lg font-semibold">{{ editingProgramId === null ? 'Create New Program' : 'Edit Program' }}</h1>
      </template>
      <template #content>
        <!-- The policy table is wider than the modal; AppModal's content area only scrolls
             vertically, so the columns need their own horizontal scroller. -->
        <div class="overflow-x-auto">
          <AccountProgramForm
            v-if="programFormMounted"
            ref="programFormRef"
            :key="programFormInstance"
            embedded
            :is-edit-mode="editingProgramId !== null"
            :account-id="accountId"
            :program-id="editingProgramId ?? undefined"
            @saved="onProgramSaved"
            @cancel="closeProgramForm" />
        </div>
      </template>
      <template #actions>
        <button
          class="btn btn-sm"
          data-testid="cancel-program"
          @click="closeProgramForm">
          Cancel
        </button>
        <button
          class="btn text-white btn-sm btn-primary"
          data-testid="save-program"
          :disabled="programFormSaving"
          @click="programFormRef?.saveProgram()">
          <span
            v-if="programFormSaving"
            class="loading mx-2 loading-sm loading-dots"></span>
          <span v-else>{{ editingProgramId === null ? 'Create' : 'Save' }} Program</span>
        </button>
      </template>
    </AppModal>
  </div>
</template>
